/**
 * Workspace — Parent component that manages the agent session
 * and always renders the canvas+chat layout.
 *
 * Layout: Always UnifiedCanvasLayout (DAG + chat sidebar).
 * Source tables auto-load on session start so the canvas is immediately
 * populated with a source group node.
 *
 * Entry point: /batches/:batchId/agent
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useSSEAwareQuery } from '../../hooks/useSSEAwareQuery';
import {
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useProjectContext } from '../../context/ProjectContext';
import {
  useCreateWorkspaceSession,
  useActiveSessionForBatch,
  useWorkspaceReady,
} from '../../hooks/useWorkspaceSession';
import type { CreateSessionParams } from '../../services/workspaceApi';
import { getBatch } from '../../services/batchApi';
import UnifiedCanvasLayout from './UnifiedCanvasLayout';

// ── Component ────────────────────────────────────────────────────────

const Workspace: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectedProjectId } = useProjectContext();
  const location = useLocation();
  const isMappingMode = location.pathname.endsWith('/mapping');

  // Cross-batch pipeline reuse: read query param set by BatchDetail
  const reusePipelineId = searchParams.get('reusePipeline') || undefined;
  const clearReusePipelineParam = useCallback(() => {
    searchParams.delete('reusePipeline');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);
  const createSession = useCreateWorkspaceSession();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [autoCreating, setAutoCreating] = useState(false);
  const [createFailed, setCreateFailed] = useState(false);
  // Ref to avoid re-triggering the auto-create effect when mutation state changes
  const createSessionRef = useRef(createSession);
  createSessionRef.current = createSession;

  // ── Recover existing session on mount ───────────────────────────
  const { data: existingSession, isLoading: recovering } =
    useActiveSessionForBatch(batchId);

  useEffect(() => {
    if (existingSession && !sessionId) {
      setSessionId(existingSession.session_id);
    }
  }, [existingSession, sessionId]);

  // ── Auto-create session if none exists ──────────────────────────
  useEffect(() => {
    if (
      !recovering &&
      !existingSession &&
      !sessionId &&
      !autoCreating &&
      !createFailed &&
      batchId &&
      user &&
      selectedProjectId
    ) {
      setAutoCreating(true);
      const params: CreateSessionParams = {
        project_id: selectedProjectId,
        tenant_id: user.tenant_id || 'default',
        user_id: user.user_id,
        batch_id: batchId,
        attachment_point: 'source',
      };
      createSessionRef.current
        .mutateAsync(params)
        .then((session) => setSessionId(session.session_id))
        .catch(() => {
          setAutoCreating(false);
          setCreateFailed(true); // Stop retrying — prevents 429 storm
        });
    }
  }, [recovering, existingSession, sessionId, autoCreating, createFailed, batchId, user, selectedProjectId]);

  // ── Fetch batch to resolve file name + track batch status ───────
  const { data: batch } = useSSEAwareQuery(
    ['batch', batchId],
    () => getBatch(batchId!),
    { enabled: !!batchId, pollingInterval: 10_000, ssePollingInterval: false, refetchOnWindowFocus: false, retry: false },
  );

  // ── Wait for workspace reconstruction (non-blocking) ──────────
  const { ready: workspaceReady } = useWorkspaceReady(sessionId);

  const batchFileName = useMemo(() => {
    if (batch?.batch_name) return batch.batch_name;
    if (batch?.files && batch.files.length > 0) {
      if (batch.files.length === 1) {
        return batch.files[0];
      }
      return `${batch.files.length} source files`;
    }
    return batchId ?? undefined;
  }, [batch, batchId]);

  // ── Loading state — session recovery or creation in progress ────
  if ((recovering || autoCreating) && !sessionId) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          {recovering ? 'Recovering session…' : 'Starting agent session…'}
        </Typography>
      </Box>
    );
  }

  // ── Fallback: session creation failed ───────────────────────────
  if (!sessionId) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography color="text.secondary">
          Unable to start agent session. Please try refreshing.
        </Typography>
      </Box>
    );
  }

  // ── Workspace still initialising in background ────────────────
  if (!workspaceReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Preparing workspace…
        </Typography>
      </Box>
    );
  }

  // ── Always show canvas layout ──────────────────────────────────
  return (
    <UnifiedCanvasLayout
      mode={isMappingMode ? 'mapping' : 'dag'}
      sessionId={sessionId}
      batchId={batchId || ''}
      batchFileName={batchFileName}
      batchStatus={batch?.status}
      batchMappingCount={batch?.mapping_count}
      batchTargetSchemaId={batch?.target_schema_id}
      reusePipelineId={reusePipelineId}
      onReusePipelineConsumed={clearReusePipelineParam}
    />
  );
};

export default Workspace;
